import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import {
  BIGQUERY_TYPES,
  buildAirflowTags,
  buildDatePredicate,
  buildInsertSqlSkeleton,
  buildSourceRef,
  buildTargetRef,
  checkInsertSql,
  ColumnDef,
  cronScheduleSchema,
  DeleteTaskConfig,
  GRANULARITY_LABELS,
  PipelineConfig,
  PipelineTasks,
  pipelineFiles,
  recommendedStartDate,
  renderChainPreview,
  renderDeleteQuery,
  replaceSourceRef,
  ScheduleGranularity,
  scheduleGranularity,
  SourceRef,
  START_DATE_GUIDE,
  TableGroup,
  TARGET_PROJECTS,
  TASK_KIND_LABELS,
  TaskKind,
} from '@internal/backstage-plugin-sql-pipeline-common';

/**
 * 파이프라인 폼 — 생성·수정 다이얼로그가 **같은 컴포넌트**를 쓴다.
 *
 * 둘로 나누지 않는 이유: 차이가 "식별자를 고칠 수 있는가" 하나뿐이고, 교차
 * 검증(스케줄 주기 ↔ 날짜 식, 생성 컬럼 ↔ INSERT 컬럼)은 양쪽에 모두 필요하다.
 * 두 벌로 두면 한쪽에만 검증이 들어가는 결함군이 생긴다 — scaffolder 폼과
 * 수정 폼이 그랬다.
 *
 * 제어 컴포넌트다. 상태는 다이얼로그가 소유한다.
 */

const useStyles = makeStyles(theme => ({
  section: { marginBottom: theme.spacing(3) },
  /** 섹션 구분선. 기본 `Divider`(1px, divider 색)는 어두운 테마에서 보이지 않았다. */
  divider: {
    height: 2,
    marginBottom: theme.spacing(3),
    backgroundColor: alpha(theme.palette.text.primary, 0.35),
  },
  row: { display: 'flex', gap: theme.spacing(2) },
  /**
   * 테이블 실행 순서. 한때 제목 줄 오른쪽 끝의 회색 드롭다운이라 눈에 띄지 않아
   * 그대로 두기 쉬웠다 — 그런데 순차·병렬은 한 테이블이 다른 테이블을 읽을 때
   * 결과를 가르는 선택이다. 제목 아래로 내리고 주 색상으로 감싼다.
   */
  executionOrder: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
  },
  executionOrderLabel: { color: theme.palette.primary.main, fontWeight: 700 },
  card: {
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  preview: {
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: 4,
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    border: `1px solid ${theme.palette.divider}`,
  },
  /**
   * 안내 박스. `warning.light` 배경 + `warning.dark` 글자는 대비가 너무 낮아
   * 읽히지 않았다(사용자 지적) — 두 값이 같은 색상 계열이라 명도 차가 작다.
   * 배경은 테마 기본으로 두고 **왼쪽 테두리 색으로만** 세기를 구분하고,
   * 글자는 테마가 대비를 보장하는 `text.primary` 를 쓴다.
   */
  noticeBase: {
    padding: theme.spacing(1.25, 1.75),
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
  },
  errorAccent: { borderLeft: `4px solid ${theme.palette.error.main}` },
  warnAccent: { borderLeft: `4px solid ${theme.palette.warning.main}` },
  noticeLabel: { fontWeight: 700, marginBottom: theme.spacing(0.5) },
}));

/**
 * 새 그룹의 원천. 비워서 시작한다 — 예시 테이블을 넣어 두면 고치지 않은 채
 * 저장되기 쉽다. 한때 `ygy-dp-dev.edw.lst_order` 를 넣었는데 그 프로젝트를 읽는
 * 활성 DAG 은 하나도 없었다.
 */
const emptySource = (): SourceRef => ({ project_id: '', data_set: '', table_id: '' });

const DEFAULT_DELETE: DeleteTaskConfig = {
  scope: 'date',
  date_column: 'event_date',
  lookback_days: 1,
};

const GRANULARITIES = Object.keys(GRANULARITY_LABELS) as ScheduleGranularity[];

const safeGranularity = (schedule: string): ScheduleGranularity | undefined => {
  try {
    return scheduleGranularity(schedule);
  } catch {
    return undefined;
  }
};

/**
 * 「넣기」 SQL 의 기본값. 폼을 열면 바로 올바른 형태가 들어 있다 —
 * 가이드가 날짜 토큰에 대해 요구하는 것이 "형태를 그대로 복사" 이고, 빈 칸에서
 * 시작하면 그 형태를 다시 틀리게 쓰는 것이 기본 경로가 된다.
 */
function skeletonFor(opts: {
  tableId: string;
  schedule: string;
  tasks: PipelineTasks;
  source?: SourceRef;
}): string {
  const del = opts.tasks.delete;
  // 「지우기」 가 테이블 전체를 지우면 전체 적재가 의도다 — 머리말에 날짜
  // 조건을 넣으면 오히려 틀린다.
  const predicate =
    del?.scope === 'all'
      ? undefined
      : buildDatePredicate({
          column: `DATE(o.${del?.date_column ?? DEFAULT_DELETE.date_column})`,
          granularity: safeGranularity(opts.schedule) ?? 'daily',
          lookbackDays: del?.lookback_days ?? DEFAULT_DELETE.lookback_days,
        });
  return buildInsertSqlSkeleton({
    targetRef: buildTargetRef(opts.tableId),
    columns: opts.tasks.create?.columns.map(c => c.name) ?? [],
    predicate,
    sourceRef: buildSourceRef(opts.source),
  });
}

/**
 * 기본 SQL 을 만드는 입력(테이블 이름·생성 컬럼·지우기 설정·스케줄)이 바뀌면
 * SQL 도 따라가야 한다 — 따라가지 않으면 컬럼을 추가해도 SQL 머리말이 「컬럼
 * 목록」 자리표시로 남는다(사용자 지적). 다만 손댄 SQL 은 덮어쓰면 안 되므로
 * **직전 입력으로 만든 기본값과 글자까지 같을 때만** 새 입력으로 다시 만든다.
 * 이번 변경이 SQL 자체를 바꿨다면(직접 고침·형태 채우기·원천 동기화) 그대로 둔다.
 */
function followSkeleton(opts: {
  prev: TableGroup;
  next: TableGroup;
  prevSchedule: string;
  nextSchedule: string;
}): TableGroup {
  const { prev, next } = opts;
  const insert = prev.tasks.insert;
  if (!insert || next.tasks.insert !== insert) return next;
  const pristine =
    insert.sql ===
    skeletonFor({
      tableId: prev.table_id,
      schedule: opts.prevSchedule,
      tasks: prev.tasks,
      source: prev.source,
    });
  if (!pristine) return next;
  const sql = skeletonFor({
    tableId: next.table_id,
    schedule: opts.nextSchedule,
    tasks: next.tasks,
    source: next.source,
  });
  return { ...next, tasks: { ...next.tasks, insert: { sql } } };
}

/**
 * 기본 그룹. 가이드의 기본형 «지우기 → 넣기» 다 — 같은 구간을 다시 돌려도
 * 중복이 쌓이지 않는다. 모듈 상수가 아니라 함수인 이유: 상수를 초기 state 로
 * 쓰면 여러 폼 인스턴스가 같은 중첩 객체를 공유한다.
 */
export function defaultGroup(opts: {
  tableId?: string;
  schedule: string;
}): TableGroup {
  const tableId = opts.tableId ?? '';
  const source = emptySource();
  const tasks: PipelineTasks = { delete: { ...DEFAULT_DELETE }, insert: { sql: '' } };
  tasks.insert = { sql: skeletonFor({ tableId, schedule: opts.schedule, tasks, source }) };
  return { table_id: tableId, tasks, source };
}

/**
 * 비어 있는 폼의 초기값. `owner` 는 다이얼로그가 채운다. `start_date` 는 기본
 * 주기의 추천값이다 — 오늘로 두면 첫 실행이 다음 주기로 밀린다.
 */
export function emptyPipelineConfig(opts: {
  owner: string;
  ownerEmail: string;
  now: Date;
}): PipelineConfig {
  const schedule = '30 8 * * *';
  return {
    team_tag: '',
    dag_folder: '',
    dag_id: '',
    description: '',
    schedule,
    // 고르게 한다. 기본값을 두면 확인 없이 그 프로젝트에 쓰는 DAG 이 생긴다.
    target: { project_id: '', data_set: '' },
    groups: [defaultGroup({ schedule })],
    group_execution: 'sequential',
    tags: [],
    owner: opts.owner,
    owner_email: opts.ownerEmail,
    start_date: recommendedStartDate(scheduleGranularity(schedule), opts.now),
    retries: 3,
    retry_delay_minutes: 5,
    execution_timeout_hours: 1,
  };
}

/**
 * 안내 박스. 세기를 **왼쪽 테두리 색 + 라벨 두 가지로** 표시한다 — 색만으로
 * 구분하면 색각 이상에서 구분이 안 되고, 배경을 진하게 깔면 글자 대비가 깨진다.
 */
const NoticeBox = (props: {
  severity: 'error' | 'warning';
  messages: string[];
}) => {
  const classes = useStyles();
  const isError = props.severity === 'error';
  return (
    <Box
      className={`${classes.noticeBase} ${
        isError ? classes.errorAccent : classes.warnAccent
      }`}
    >
      <Typography variant="caption" className={classes.noticeLabel} component="div">
        {isError ? '고쳐야 저장됩니다' : '확인해주세요 (저장은 됩니다)'}
      </Typography>
      {props.messages.map(message => (
        <Typography
          key={message}
          variant="body2"
          style={{ whiteSpace: 'pre-wrap' }}
        >
          {message}
        </Typography>
      ))}
    </Box>
  );
};

const CUSTOM_PROJECT = '__custom__';

/**
 * 프로젝트 칸 — 목록에서 고르거나 「직접 입력」 을 골라 옆 칸에 적는다.
 *
 * 한때 freeSolo Autocomplete 였는데, 타이핑해도 된다는 것이 보이지 않아 목록에
 * 묶인 칸으로 읽혔다(사용자 지적). 직접 입력을 선택지 하나로 드러낸다.
 *
 * 값이 목록 밖이면(목록이 생기기 전 파이프라인의 수정 폼 등) 처음부터 직접 입력
 * 상태로 그 값을 보여준다 — 선택지에 없다고 빈 칸으로 보이면 다른 것을 골라
 * 쓰는 곳이 조용히 바뀐다.
 */
const ProjectPicker = (props: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
}) => {
  const { id, label, value, onChange, error } = props;
  const inList = (TARGET_PROJECTS as readonly string[]).includes(value);
  // 직접 입력을 고른 직후는 값이 비었거나 목록 값 그대로라 값만으로는 알 수 없다.
  const [customChosen, setCustomChosen] = useState(false);
  const custom = customChosen || (value !== '' && !inList);
  return (
    // 직접 입력이면 선택 칸과 입력 칸 두 개가 들어가므로 옆 칸의 두 배를 차지한다.
    <Box display="flex" style={{ gap: 8, flex: custom ? 2 : 1, minWidth: 0 }}>
      <TextField
        id={`${id}-select`}
        select
        label={label}
        value={custom ? CUSTOM_PROJECT : value}
        onChange={e => {
          const next = e.target.value;
          setCustomChosen(next === CUSTOM_PROJECT);
          if (next !== CUSTOM_PROJECT) onChange(next);
        }}
        required
        variant="outlined"
        size="small"
        style={custom ? { width: 150, flexShrink: 0 } : { flex: 1 }}
        error={!custom && Boolean(error)}
        helperText={custom ? undefined : error}
      >
        {TARGET_PROJECTS.map(p => (
          <MenuItem key={p} value={p}>
            {p}
          </MenuItem>
        ))}
        <MenuItem value={CUSTOM_PROJECT}>
          <em>직접 입력</em>
        </MenuItem>
      </TextField>
      {custom && (
        <TextField
          id={id}
          label={`${label} id`}
          value={value}
          onChange={e => onChange(e.target.value.trim())}
          required
          variant="outlined"
          size="small"
          style={{ flex: 1 }}
          placeholder="my-gcp-project"
          error={Boolean(error)}
          helperText={error}
        />
      )}
    </Box>
  );
};

/** 테이블 하나와 그 테이블에 대한 생성·지우기·넣기. */
const TableGroupCard = (props: {
  index: number;
  group: TableGroup;
  /** 매크로가 치환될 값. 테이블 칸 아래에 해석된 전체 경로를 보여준다. */
  target: PipelineConfig['target'];
  schedule: string;
  granularity?: ScheduleGranularity;
  errors?: Record<string, string>;
  removable: boolean;
  onChange: (group: TableGroup) => void;
  onRemove: () => void;
}) => {
  const classes = useStyles();
  const { index, group, target, schedule, granularity, errors, removable } = props;
  const err = (...path: string[]) =>
    errors?.[['groups', String(index), ...path].join('.')];

  const change = (next: TableGroup) =>
    props.onChange(
      followSkeleton({ prev: group, next, prevSchedule: schedule, nextSchedule: schedule }),
    );
  const setTasks = (patch: Partial<PipelineTasks>) =>
    change({ ...group, tasks: { ...group.tasks, ...patch } });
  const setTableId = (tableId: string) => change({ ...group, table_id: tableId });

  /**
   * 원천 조각 하나가 바뀌면 SQL 안의 직전 원천 식을 새 식으로 바꾼다
   * (`replaceSourceRef`). 테이블 이름과 달리 SQL 을 고친 뒤에도 따라간다.
   */
  const setSourcePart = (key: keyof SourceRef, value: string) => {
    const prev = group.source;
    if (prev?.[key] === value) return;
    const next: SourceRef = { ...(prev ?? emptySource()), [key]: value };
    const insert = group.tasks.insert;
    const replaced = insert ? replaceSourceRef(insert.sql, prev, next) : undefined;
    props.onChange({
      ...group,
      source: next,
      tasks:
        insert && replaced?.found
          ? { ...group.tasks, insert: { ...insert, sql: replaced.sql } }
          : group.tasks,
    });
  };
  // 원천을 정했는데 SQL 에 그 식이 없으면 손으로 다른 경로를 적은 것이다 —
  // 원천을 바꿔도 SQL 이 따라가지 않으므로 알린다.
  const sourceMissingInSql = Boolean(
    group.source &&
      group.tasks.insert &&
      !group.tasks.insert.sql.includes(buildSourceRef(group.source)),
  );

  const toggle = (kind: TaskKind, on: boolean) => {
    if (!on) {
      const next = { ...group.tasks };
      delete next[kind];
      change({ ...group, tasks: next });
      return;
    }
    if (kind === 'create') {
      setTasks({ create: { columns: [], partition_by: '' } });
    } else if (kind === 'delete') {
      setTasks({ delete: { ...DEFAULT_DELETE } });
    } else {
      setTasks({
        insert: {
          sql: skeletonFor({
            tableId: group.table_id,
            schedule,
            tasks: group.tasks,
            source: group.source,
          }),
        },
      });
    }
  };

  const sqlIssues =
    group.tasks.insert && granularity
      ? checkInsertSql({
          sql: group.tasks.insert.sql,
          granularity,
          tableId: group.table_id,
          columns: group.tasks.create?.columns.map(c => c.name),
          requireIntervalTokens: group.tasks.delete?.scope !== 'all',
        })
      : [];
  const sqlErrors = sqlIssues.filter(i => i.severity === 'error');
  const sqlWarnings = sqlIssues.filter(i => i.severity === 'warning');

  const updateColumn = (i: number, patch: Partial<ColumnDef>) =>
    setTasks({
      create: {
        ...group.tasks.create!,
        columns: group.tasks.create!.columns.map((c, j) =>
          j === i ? { ...c, ...patch } : c,
        ),
      },
    });

  return (
    <Card className={classes.card} variant="outlined">
      <Box className={classes.cardHeader}>
        <TextField
          label={`테이블 ${index + 1}`}
          value={group.table_id}
          onChange={e => setTableId(e.target.value)}
          required
          variant="outlined"
          size="small"
          style={{ minWidth: 380, backgroundColor: 'transparent' }}
          placeholder="daily_order_summary"
          error={Boolean(err('table_id'))}
          // SQL 은 `{{ PROJECT_ID }}.{{ DATA_SET }}.{테이블}` 로 적히는데, 앞의
          // 두 조각이 위 「결과를 넣을 테이블」 에서 온다는 것이 폼에서 보이지
          // 않았다. 해석된 전체 경로를 여기 적어 그 연결을 드러낸다.
          helperText={
            err('table_id') ??
            (group.table_id
              ? `→ ${target.project_id || '{프로젝트}'}.${
                  target.data_set || '{데이터셋}'
                }.${group.table_id}`
              : undefined)
          }
        />
        {removable && (
          <IconButton size="small" onClick={props.onRemove}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <CardContent className={classes.fields}>
        {err('tasks') && <NoticeBox severity="error" messages={[err('tasks')!]} />}

        {/* 생성 */}
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(group.tasks.create)}
                onChange={e => toggle('create', e.target.checked)}
                color="primary"
              />
            }
            label={`${TASK_KIND_LABELS.create} — 테이블이 아직 없을 때만`}
          />
          {group.tasks.create && (
            <Box className={classes.fields} pt={1}>
              <Typography variant="caption" color="textSecondary">
                파티션은 <strong>나중에 바꿀 수 없습니다</strong>. 데이터셋은 BigQuery
                에 미리 만들어져 있어야 합니다 — DAG 이 만들어 주지 않습니다.
              </Typography>
              {group.tasks.create.columns.map((column, i) => (
                <Box key={i} className={classes.row} alignItems="center">
                  <TextField
                    id={`group-${index}-column-${i}`}
                    label="컬럼"
                    value={column.name}
                    onChange={e => updateColumn(i, { name: e.target.value })}
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                  <TextField
                    select
                    label="타입"
                    value={column.type}
                    onChange={e => updateColumn(i, { type: e.target.value as never })}
                    variant="outlined"
                    size="small"
                    style={{ minWidth: 130 }}
                  >
                    {BIGQUERY_TYPES.map(t => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </TextField>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(column.required)}
                        onChange={e => updateColumn(i, { required: e.target.checked })}
                        size="small"
                      />
                    }
                    label="필수"
                  />
                  <IconButton
                    size="small"
                    onClick={() =>
                      setTasks({
                        create: {
                          ...group.tasks.create!,
                          columns: group.tasks.create!.columns.filter(
                            (_, j) => j !== i,
                          ),
                        },
                      })
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Box className={classes.row}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() =>
                    setTasks({
                      create: {
                        ...group.tasks.create!,
                        columns: [
                          ...group.tasks.create!.columns,
                          { name: '', type: 'STRING' },
                        ],
                      },
                    })
                  }
                  variant="outlined"
                  size="small"
                >
                  컬럼 추가
                </Button>
                <TextField
                  select
                  label="파티션 기준"
                  value={group.tasks.create.partition_by}
                  onChange={e =>
                    setTasks({
                      create: {
                        ...group.tasks.create!,
                        partition_by: e.target.value,
                      },
                    })
                  }
                  variant="outlined"
                  size="small"
                  style={{ minWidth: 200 }}
                  error={Boolean(err('tasks', 'create', 'partition_by'))}
                  helperText={
                    err('tasks', 'create', 'partition_by') ??
                    (group.tasks.create.partition_by
                      ? undefined
                      : '조회가 매번 전체 스캔이 됩니다')
                  }
                >
                  {/* 파티션 없는 테이블도 정상이다 — 작은 매핑·차원 테이블. */}
                  <MenuItem value="">없음 (파티션 안 함)</MenuItem>
                  {group.tasks.create.columns
                    .filter(c => ['DATE', 'DATETIME', 'TIMESTAMP'].includes(c.type))
                    .map(c => (
                      <MenuItem key={c.name} value={c.name}>
                        {c.name}
                      </MenuItem>
                    ))}
                </TextField>
              </Box>
            </Box>
          )}
        </Box>

        {/* 지우기 */}
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(group.tasks.delete)}
                onChange={e => toggle('delete', e.target.checked)}
                color="primary"
              />
            }
            label={`${TASK_KIND_LABELS.delete} — 몇 번 재실행해도 결과가 같아집니다`}
          />
          {group.tasks.delete && (
            <Box className={classes.fields} pt={1}>
              <Box className={classes.row}>
                <TextField
                  select
                  label="지울 범위"
                  value={group.tasks.delete.scope}
                  onChange={e =>
                    setTasks({
                      delete: { ...group.tasks.delete!, scope: e.target.value as never },
                    })
                  }
                  variant="outlined"
                  size="small"
                  style={{ minWidth: 260 }}
                >
                  <MenuItem value="date">이번 구간의 날짜만</MenuItem>
                  <MenuItem value="all">테이블 전체 — 매번 전체 다시 넣기</MenuItem>
                </TextField>
                {group.tasks.delete.scope === 'date' && (
                  <TextField
                    label="기준 날짜 컬럼"
                    value={group.tasks.delete.date_column}
                    onChange={e =>
                      setTasks({
                        delete: {
                          ...group.tasks.delete!,
                          date_column: e.target.value,
                        },
                      })
                    }
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={Boolean(err('tasks', 'delete', 'date_column'))}
                    helperText={err('tasks', 'delete', 'date_column')}
                  />
                )}
                {group.tasks.delete.scope === 'date' && granularity === 'daily' && (
                  <TextField
                    label="며칠치를 다시 계산"
                    type="number"
                    value={group.tasks.delete.lookback_days}
                    onChange={e =>
                      setTasks({
                        delete: {
                          ...group.tasks.delete!,
                          lookback_days: Math.max(
                            1,
                            Math.min(31, Number(e.target.value)),
                          ),
                        },
                      })
                    }
                    variant="outlined"
                    size="small"
                    // 옆 「기준 날짜 컬럼」 이 fullWidth 라 줄어들 수 있으면 눌려서
                    // 라벨과 도움말이 여러 줄로 꺾였다.
                    style={{ width: 250, flexShrink: 0 }}
                    inputProps={{ min: 1, max: 31 }}
                    helperText="늦게 들어오는 데이터가 있으면 늘리세요"
                  />
                )}
              </Box>
              <Typography variant="caption" color="textSecondary">
                {group.tasks.delete.scope === 'all' ? (
                  <>
                    DELETE 쿼리는 자동 생성됩니다 — <code>WHERE TRUE</code> 로 전체를
                    지운 뒤 다시 넣습니다. 파티션이 없는 작은 테이블에 맞고, 데이터가
                    많으면 매번 전체를 다시 읽게 되니 「이번 구간의 날짜만」 을
                    쓰세요.
                  </>
                ) : (
                  <>
                    DELETE 쿼리는 자동 생성됩니다 — 날짜 식이 스케줄 주기에 따라
                    달라지고 (일배치는 어제, 주배치는 7일 전, 월배치는 전월 1일) 여기서
                    틀리면 «실행은 성공인데 0 건» 이 됩니다. 아래 SQL 의 WHERE 범위를
                    이것과 같게 맞춰주세요.
                  </>
                )}
              </Typography>
              {/*
                실제로 conf yaml 에 들어갈 쿼리를 같은 함수로 만들어 보여준다 —
                설명만으로는 날짜 식이 어떻게 풀리는지 알 수 없어 아래 INSERT 의
                WHERE 를 맞추기 어려웠다(사용자 지적).
              */}
              <Box className={classes.preview}>
                {group.tasks.delete.scope === 'date' && !granularity
                  ? '실행 시각의 Cron 이 올바르면 DELETE 쿼리가 여기 보입니다'
                  : renderDeleteQuery({
                      targetRef: buildTargetRef(group.table_id),
                      scope: group.tasks.delete.scope,
                      dateColumn: group.tasks.delete.date_column || '{날짜 컬럼}',
                      schedule,
                      lookbackDays: group.tasks.delete.lookback_days,
                    })}
              </Box>
              {err('tasks', 'delete') && (
                <NoticeBox severity="error" messages={[err('tasks', 'delete')!]} />
              )}
            </Box>
          )}
        </Box>

        {/* 넣기 */}
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(group.tasks.insert)}
                  onChange={e => toggle('insert', e.target.checked)}
                  color="primary"
                />
              }
              label={`${TASK_KIND_LABELS.insert} — 내 SQL`}
            />
            {group.tasks.insert && (
              // 테두리 없는 글자 버튼은 제목 줄의 설명처럼 보여 누를 수 있는 줄
              // 몰랐다(사용자 지적).
              <Box display="flex" style={{ gap: 8 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    setTasks({
                      insert: {
                        sql: skeletonFor({
                          tableId: group.table_id,
                          schedule,
                          tasks: group.tasks,
                          source: group.source,
                        }),
                      },
                    })
                  }
                >
                  형태 채우기
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setTasks({ insert: { sql: '' } })}
                >
                  모두 지우기
                </Button>
              </Box>
            )}
          </Box>
          {group.tasks.insert && (
            <Box className={classes.fields} pt={1}>
              {/*
                데이터를 가져올 테이블 — 결과를 넣을 테이블과 같은 모양으로 받는다.
                SQL 에 원천 경로를 손으로 적으면 백틱·별칭·프로젝트 표기가 DAG 마다
                달라진다. 원천 프로젝트는 대상보다 다양하다(실측: rgpkorea-mkt·
                ygy-lite-datawarehouse 등) — 직접 입력이 자주 쓰인다.
              */}
              <Typography variant="subtitle2">데이터를 가져올 테이블</Typography>
              <Typography variant="caption" color="textSecondary">
                바꾸면 아래 SQL 의 원천 경로도 같이 바뀝니다. JOIN 하는 다른 테이블은
                SQL 에 직접 적어주세요.
              </Typography>
              <Box className={classes.row}>
                {/* id 가 있어야 라벨이 입력란과 연결된다(MUI v4) — 그룹마다 달라야 한다. */}
                <ProjectPicker
                  id={`group-${index}-source-project`}
                  label="원천 프로젝트"
                  value={group.source?.project_id ?? ''}
                  onChange={value => setSourcePart('project_id', value)}
                  error={err('source', 'project_id')}
                />
                <TextField
                  id={`group-${index}-source-dataset`}
                  label="원천 데이터셋"
                  value={group.source?.data_set ?? ''}
                  onChange={e => setSourcePart('data_set', e.target.value)}
                  required
                  variant="outlined"
                  size="small"
                  style={{ flex: 1 }}
                  placeholder="edw"
                  error={Boolean(err('source', 'data_set'))}
                  helperText={err('source', 'data_set')}
                />
                <TextField
                  id={`group-${index}-source-table`}
                  label="원천 테이블"
                  value={group.source?.table_id ?? ''}
                  onChange={e => setSourcePart('table_id', e.target.value)}
                  required
                  variant="outlined"
                  size="small"
                  style={{ flex: 1 }}
                  placeholder="lst_order"
                  error={Boolean(err('source', 'table_id'))}
                  helperText={err('source', 'table_id')}
                />
              </Box>
              {sourceMissingInSql && (
                <NoticeBox
                  severity="warning"
                  messages={[
                    `SQL 에 원천 경로 ${buildSourceRef(group.source)} 가 없습니다 — 원천을 바꿔도 SQL 이 따라가지 않습니다. 「형태 채우기」로 다시 만들 수 있습니다.`,
                  ]}
                />
              )}
              <TextField
                label="SQL"
                multiline
                rows={14}
                value={group.tasks.insert.sql}
                onChange={e => setTasks({ insert: { sql: e.target.value } })}
                fullWidth
                required
                variant="outlined"
                size="small"
                InputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
              />
              {/*
                차단과 경고를 **나눠서** 보여준다. 섞어 두면 저장이 왜 막히는지
                알 수 없고, 사용자가 경고를 고치려고 시간을 쓴다.
              */}
              {sqlErrors.length > 0 && (
                <NoticeBox
                  severity="error"
                  messages={sqlErrors.map(i => i.message)}
                />
              )}
              {sqlWarnings.length > 0 && (
                <NoticeBox
                  severity="warning"
                  messages={sqlWarnings.map(i => i.message)}
                />
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------

export type PipelineFormProps = {
  config: PipelineConfig;
  onChange: (config: PipelineConfig) => void;
  /** 경로(`groups.0.tasks.insert.sql`) → 메시지 */
  errors?: Record<string, string>;
  /** 생성 폼만 식별자(팀·폴더·dag_id)를 고칠 수 있다. */
  mode: 'create' | 'edit';
  /**
   * 고를 수 있는 팀. **로그인 사용자가 속한 Keycloak 그룹 ∩ `TEAM_TAGS`**
   * (`teamTagsFromOwnership`). 위키의 「내 팀 폴더 안에서만 작업하세요」 가
   * 폼에서 강제된다.
   *
   * 비어 있으면 **DAG 팀이 배정되지 않은 사용자**다. 두 경우가 있고 안내 문구가
   * 다르므로 `ownedGroups` 로 가른다.
   * 상수 목록으로 기본값을 주지 않는 이유: 그러면 그룹 0 개 사용자에게 전체
   * 목록이 보이고, 남의 팀으로 PR 을 올린 뒤 리뷰에서야 막힌다.
   */
  availableTeams: string[];
  /**
   * 교집합 **전**의 내 Keycloak 그룹(`groupTagsFromOwnership`). `availableTeams` 가
   * 비었을 때 그 이유를 가르는 데만 쓴다 — 그룹조차 없는 것과, 그룹은 있으나
   * `TEAM_TAGS` 에 없는 것은 요청해야 할 내용이 다르다.
   *
   * 생략하면 빈 배열로 본다. 수정 폼은 `availableTeams` 가 항상 1 개라 이 분기에
   * 닿지 않으므로 넘기지 않는다.
   */
  ownedGroups?: string[];
};

export const PipelineForm = ({
  config,
  onChange,
  errors,
  mode,
  availableTeams,
  ownedGroups = [],
}: PipelineFormProps) => {
  const classes = useStyles();
  const set = (patch: Partial<PipelineConfig>) => onChange({ ...config, ...patch });

  const identifiersLocked = mode === 'edit';
  const granularity = safeGranularity(config.schedule);
  const scheduleValid = cronScheduleSchema.safeParse(config.schedule).success;
  const files =
    config.team_tag && config.dag_id && config.dag_folder
      ? pipelineFiles({
          teamTag: config.team_tag,
          dagFolder: config.dag_folder,
          dagId: config.dag_id,
          insertTableIds: config.groups
            .filter(g => g.tasks.insert && g.table_id)
            .map(g => g.table_id),
        })
      : undefined;

  const prefix = config.team_tag ? `${config.team_tag}_dag_` : '';
  const dagIdSuffix = config.dag_id.startsWith(prefix)
    ? config.dag_id.slice(prefix.length)
    : config.dag_id;

  /** 팀이 바뀌면 dag_id 접두사와 태그 첫 원소가 따라온다. */
  const setTeam = (teamTag: string) => {
    // 직전 팀 태그만 걷어낸다. `TEAM_TAGS` 상수로 걸러내면 Keycloak 에 팀이
    // 추가될 때 이 레포의 목록도 고쳐야 한다 — 그룹이 곧 권한이므로 상수에
    // 의존하지 않는다.
    const extra = config.tags.filter(t => t !== config.team_tag);
    onChange({
      ...config,
      team_tag: teamTag,
      dag_id: dagIdSuffix ? `${teamTag}_dag_${dagIdSuffix}` : '',
      tags: buildAirflowTags({ teamTag, extra }),
    });
  };

  /**
   * 주기가 바뀌면 날짜 식이 달라지므로 손대지 않은 SQL 은 따라간다. 시작일도
   * 어느 주기의 추천값 그대로면(= 고르지 않았으면) 새 주기의 추천값으로 간다.
   * 직전 주기의 추천값과만 비교하면 한 글자씩 고치는 중 cron 이 잠시 깨질 때
   * 연결이 끊긴다.
   */
  const setSchedule = (next: string) => {
    const now = new Date();
    const nextG = safeGranularity(next);
    const untouched = GRANULARITIES.some(
      g => config.start_date === recommendedStartDate(g, now),
    );
    set({
      schedule: next,
      start_date:
        nextG && untouched ? recommendedStartDate(nextG, now) : config.start_date,
      groups: config.groups.map(g =>
        followSkeleton({
          prev: g,
          next: { ...g },
          prevSchedule: config.schedule,
          nextSchedule: next,
        }),
      ),
    });
  };

  const setGroup = (idx: number, next: TableGroup) =>
    set({ groups: config.groups.map((g, i) => (i === idx ? next : g)) });

  // 팀이 하나뿐이면 고를 이유가 없다. 드롭다운은 비활성으로 두고 값만 채운다 —
  // 숨기면 어느 팀으로 만들어지는지 보이지 않는다.
  //
  // 렌더 중에 onChange 를 부르면 "Cannot update a component while rendering a
  // different component" 가 된다. effect 로 미룬다.
  const onlyTeam = availableTeams.length === 1 ? availableTeams[0] : undefined;
  useEffect(() => {
    if (mode !== 'create' || !onlyTeam || config.team_tag === onlyTeam) return;
    setTeam(onlyTeam);
    // setTeam 은 config 를 닫아 잡으므로 의존성에 넣으면 매 렌더 재생성된다.
    // 팀 값이 목표와 다를 때만 한 번 돌면 되므로 아래 두 값만 본다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, onlyTeam, config.team_tag]);

  return (
    <Box>
      {/*
        DAG 팀이 배정되지 않은 사용자. 빈 드롭다운만 보여주면 무엇이 잘못됐는지
        알 수 없으므로 안내를 띄운다. 두 경우는 @etl 에게 요청할 내용이 다르다.

        ① 그룹 0 개 — Keycloak 그룹 배정이 수동이라 첫 로그인과 배정 사이에 반드시
           이 구간이 생긴다. 요청할 것은 「그룹에 넣어주세요」 다.
        ② 그룹은 있으나 `TEAM_TAGS` 에 없음 — realm 이 Airflow·OpenMetadata·AKHQ
           공유라 `om-dpe`·`svc-readonly` 처럼 DAG 팀이 아닌 그룹이 섞여 있다.
           신설 팀이라면 `dp-lakehouse` 의 `dags-{team}/` 과 이 레포의 `TEAM_TAGS`
           양쪽이 필요하므로, 요청할 것은 「TEAM TAG 를 만들어주세요」 다.
      */}
      {availableTeams.length === 0 && (
        <Box className={classes.section}>
          <NoticeBox
            severity="error"
            messages={[
              ownedGroups.length === 0
                ? '할당된 그룹이 없습니다. #help_data 에서 @etl 을 멘션해 팀 배정을 요청해주세요.'
                : 'backstage 에 TEAM TAG 가 생성되지 않았습니다. #help_data 에서 @etl 을 멘션해 팀 배정을 요청해주세요.',
            ]}
          />
        </Box>
      )}

      {/* ── 기본 정보 ─────────────────────────────────────────────── */}
      <Box className={classes.section}>
        <Typography variant="subtitle2" gutterBottom>
          기본 정보
        </Typography>
        <Box className={classes.row} mb={2}>
          <TextField
            select
            label="내 팀"
            value={config.team_tag}
            onChange={e => setTeam(e.target.value)}
            disabled={identifiersLocked || availableTeams.length <= 1}
            required
            variant="outlined"
            size="small"
            style={{ minWidth: 130 }}
            error={Boolean(errors?.team_tag)}
            helperText={errors?.team_tag}
          >
            {/* 수정 폼은 자기 팀 하나만 들고 온다 — 팀을 바꾸는 것은 파일 이동이다. */}
            {availableTeams.map(t => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="폴더 경로"
            value={config.dag_folder}
            onChange={e => set({ dag_folder: e.target.value })}
            disabled={identifiersLocked}
            required
            fullWidth
            variant="outlined"
            size="small"
            placeholder="tech/product_metric/daily_order_summary"
            error={Boolean(errors?.dag_folder)}
            helperText={
              errors?.dag_folder ??
              `dags-${config.team_tag.toLowerCase() || '{팀}'} 아래 경로. / 를 넣으면 하위 폴더가 됩니다`
            }
          />
        </Box>
        <TextField
          label="dag id"
          value={dagIdSuffix}
          onChange={e =>
            set({ dag_id: config.team_tag ? `${prefix}${e.target.value}` : e.target.value })
          }
          disabled={identifiersLocked || !config.team_tag}
          required
          fullWidth
          variant="outlined"
          size="small"
          placeholder="daily_order_summary"
          InputProps={{
            startAdornment: prefix ? (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ fontFamily: 'monospace', marginRight: 2 }}
              >
                {prefix}
              </Typography>
            ) : undefined,
          }}
          error={Boolean(errors?.dag_id)}
          helperText={
            errors?.dag_id ??
            'Airflow 내에서 하나뿐인 이름이어야 합니다. dag id는 파일명으로 작성됩니다.'
          }
          style={{ marginBottom: 16 }}
        />
        {files && (
          <Box className={classes.preview} mb={2}>
            {[
              `${files.dir}/`,
              `├── ${config.dag_id}.py`,
              `├── conf/${config.dag_id}.yaml`,
              ...files.sqlPaths.map(s => `├── sql/${s.include}`),
            ].join('\n')}
          </Box>
        )}
        {/*
          선택값이다. 비우면 미리보기·저장 직전에 티켓 링크로 채운다
          (withDescriptionFallback) — dp-lakehouse 의 DAG 무결성 테스트가 dags-dp
          의 빈 설명을 실패시키므로 실제 파일에는 늘 무언가 들어간다.
        */}
        <TextField
          label="설명"
          value={config.description}
          onChange={e => set({ description: e.target.value })}
          fullWidth
          variant="outlined"
          size="small"
          error={Boolean(errors?.description)}
          helperText={errors?.description ?? '비워 두면 티켓 링크가 들어갑니다'}
        />
      </Box>

      {/* ── 실행 시각 ─────────────────────────────────────────────── */}
      <Box className={classes.section}>
        <Typography variant="subtitle2" gutterBottom>
          실행 시각 (한국 시간)
        </Typography>
        <Box className={classes.row}>
          <TextField
            id="schedule-cron"
            label="Cron"
            value={config.schedule}
            onChange={e => setSchedule(e.target.value)}
            required
            fullWidth
            variant="outlined"
            size="small"
            error={Boolean(errors?.schedule) || !scheduleValid}
            helperText={
              errors?.schedule ??
              (scheduleValid
                ? `분 시 일 월 요일 — ${granularity ? GRANULARITY_LABELS[granularity] : ''}`
                : 'cron 5 필드로 적어주세요 (예: 30 8 * * *)')
            }
          />
          <TextField
            label="재시도"
            type="number"
            value={config.retries}
            onChange={e =>
              set({ retries: Math.max(0, Math.min(5, Number(e.target.value))) })
            }
            variant="outlined"
            size="small"
            style={{ width: 110 }}
            inputProps={{ min: 0, max: 5 }}
          />
          <TextField
            label="task 제한시간 (hr)"
            type="number"
            value={config.execution_timeout_hours}
            onChange={e =>
              set({
                execution_timeout_hours: Math.max(
                  0.25,
                  Math.min(12, Number(e.target.value)),
                ),
              })
            }
            variant="outlined"
            size="small"
            style={{ width: 200 }}
            inputProps={{ min: 0.25, max: 12, step: 0.25 }}
            helperText="평균 소요시간의 2~3배 추천"
            FormHelperTextProps={{ style: { whiteSpace: 'nowrap' } }}
          />
        </Box>
        <Box className={classes.row} mt={2} alignItems="flex-start">
          <TextField
            id="start-date"
            label="시작일 (start_date)"
            type="date"
            value={config.start_date}
            onChange={e => set({ start_date: e.target.value })}
            required
            variant="outlined"
            size="small"
            style={{ width: 200, flexShrink: 0 }}
            InputLabelProps={{ shrink: true }}
            error={Boolean(errors?.start_date)}
            helperText={errors?.start_date}
          />
          {/* 날짜 칸 아래 helperText 로 두면 200px 폭에 여러 줄로 접힌다. */}
          <Typography variant="caption" color="textSecondary" component="div">
            {START_DATE_GUIDE}
          </Typography>
        </Box>
      </Box>

      {/* ── 태그 ──────────────────────────────────────────────────── */}
      <Box className={classes.section}>
        <TextField
          label="태그 (쉼표 구분)"
          value={config.tags.slice(1).join(', ')}
          onChange={e =>
            set({
              tags: buildAirflowTags({
                teamTag: config.team_tag,
                extra: e.target.value
                  .split(',')
                  .map(t => t.trim())
                  .filter(Boolean),
              }),
            })
          }
          fullWidth
          variant="outlined"
          size="small"
          error={Boolean(errors?.tags)}
          helperText={
            errors?.tags ?? 'Airflow 화면 검색용. 팀 약자는 자동으로 들어갑니다'
          }
        />
        <Box mt={1} display="flex" style={{ gap: 8, flexWrap: 'wrap' }}>
          {config.tags.map(t => (
            <Chip key={t} label={t} size="small" />
          ))}
        </Box>
      </Box>

      {/*
        구분선 — 섹션 제목이 작은 subtitle2 라 칸들이 한 덩어리로 읽혔다(사용자
        지적). 「언제·무엇으로」 와 「무엇을·어디에」 사이에 긋는다.
      */}
      <Divider className={classes.divider} />

      {/* ── 테이블 그룹 ───────────────────────────────────────────── */}
      <Typography variant="subtitle2" gutterBottom>
        무엇을 할까요 — 테이블 {config.groups.length}개
      </Typography>

      {/* ── 공통 프로젝트·데이터셋 ─────────────────────────────────── */}
      <Box className={classes.section} mt={1.5}>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          결과를 넣을 테이블
        </Typography>
        <Box className={classes.row}>
          <ProjectPicker
            id="target-project"
            label="프로젝트"
            value={config.target.project_id}
            onChange={value => set({ target: { ...config.target, project_id: value } })}
            error={errors?.['target.project_id']}
          />
          <TextField
            label="데이터셋"
            value={config.target.data_set}
            onChange={e =>
              set({ target: { ...config.target, data_set: e.target.value } })
            }
            required
            variant="outlined"
            size="small"
            style={{ flex: 1 }}
            error={Boolean(errors?.['target.data_set'])}
            helperText={
              errors?.['target.data_set'] ??
              'BigQuery 에 미리 있어야 합니다 — DAG 이 만들어 주지 않습니다'
            }
          />
        </Box>
      </Box>

      {config.groups.length > 1 && (
        <Box className={classes.executionOrder}>
          <TextField
            select
            label="테이블 실행 순서"
            value={config.group_execution}
            onChange={e => set({ group_execution: e.target.value as never })}
            variant="outlined"
            size="small"
            color="primary"
            style={{ minWidth: 260 }}
            InputLabelProps={{ className: classes.executionOrderLabel }}
          >
            <MenuItem value="sequential">순차 — A 를 끝낸 뒤 B</MenuItem>
            <MenuItem value="parallel">병렬 — 동시에</MenuItem>
          </TextField>
          <Typography variant="caption">
            뒤 테이블이 앞 테이블의 결과를 읽는다면 <strong>순차</strong>여야 합니다.
            서로 무관하면 병렬로 빨리 끝낼 수 있습니다.
          </Typography>
        </Box>
      )}
      <Box className={classes.preview} mb={2}>
        {renderChainPreview(config.groups, config.group_execution)}
      </Box>
      {errors?.groups && (
        <Box mb={2}>
          <NoticeBox severity="error" messages={[errors.groups]} />
        </Box>
      )}

      {config.groups.map((group, idx) => (
        <TableGroupCard
          key={idx}
          index={idx}
          group={group}
          target={config.target}
          schedule={config.schedule}
          granularity={granularity}
          errors={errors}
          removable={config.groups.length > 1}
          onChange={next => setGroup(idx, next)}
          onRemove={() =>
            set({ groups: config.groups.filter((_, i) => i !== idx) })
          }
        />
      ))}

      <Button
        startIcon={<AddIcon />}
        onClick={() =>
          set({
            groups: [
              ...config.groups,
              defaultGroup({ schedule: config.schedule }),
            ],
          })
        }
        variant="outlined"
        size="small"
      >
        테이블 추가
      </Button>
    </Box>
  );
};
